import { NamiCampaign, NamiInitialConfig } from "@namiml/web-sdk";
import { testPaywallFiles } from '../data/test-paywalls';

type FullInitialConfig = Exclude<NamiInitialConfig, { initial_config: string }>;
type NamiPaywalls = FullInitialConfig['paywalls'];
type NamiPaywall = NonNullable<NamiPaywalls>[number];

const API_CAMPAIGN_RULES = "api_campaign_rules";
const API_PAYWALLS = "api_paywalls";
// Since NAM-4357 a per-URL resolution stores each paywall body under its own key and writes
// api_paywalls as `{ bodyCacheUrls }` rather than whole objects. Mirrors sdk/core's
// services/paywallBodyCache.ts; the metadata key is what makes the entry a valid one.
const PAYWALL_BODY_KEY_PREFIX = "api_paywall_body:";
const PAYWALL_BODY_META_KEY_PREFIX = "api_paywall_body_meta:";
const LOCAL_PAYWALL_URL_PREFIX = "local-test-paywall://paywall/";

function simpleUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

export function augmentStoredData() {
  const rawCampaigns = JSON.parse(localStorage.getItem(API_CAMPAIGN_RULES) ?? 'null');
  const rawPaywalls = JSON.parse(localStorage.getItem(API_PAYWALLS) ?? 'null');

  // Unwrap TTL-wrapped format { data, cachedAt } if present
  let campaigns: NamiCampaign[];
  let campaignsTtlWrapped = false;
  if (rawCampaigns && !Array.isArray(rawCampaigns) && Array.isArray(rawCampaigns.data)) {
    campaigns = rawCampaigns.data;
    campaignsTtlWrapped = true;
  } else if (Array.isArray(rawCampaigns)) {
    campaigns = rawCampaigns;
  } else {
    return;
  }

  // Campaigns are only injected when their paywalls can be too — otherwise they would name missing
  // paywalls (e.g. under campaign_cache_ttl 0, when nothing is in localStorage).
  const bodyCacheUrls: string[] | null =
    rawPaywalls && !Array.isArray(rawPaywalls) && Array.isArray(rawPaywalls.bodyCacheUrls)
      ? rawPaywalls.bodyCacheUrls
      : null;
  const legacyPaywalls = !bodyCacheUrls && (Array.isArray(rawPaywalls) || Array.isArray(rawPaywalls?.data));
  if (!bodyCacheUrls && !legacyPaywalls) return;

  const campaignValues = new Set(campaigns.map((campaign: NamiCampaign) => campaign.value));
  const newCampaigns = createCampaigns(testPaywallFiles);
  for (const newCampaign of newCampaigns) {
    if (!campaignValues.has(newCampaign.value)) {
      campaigns.push(newCampaign);
    }
  }
  const campaignsToStore = campaignsTtlWrapped
    ? { data: campaigns, cachedAt: Date.now() }
    : campaigns;
  localStorage.setItem(API_CAMPAIGN_RULES, JSON.stringify(campaignsToStore));

  if (bodyCacheUrls) {
    augmentBodyCache(bodyCacheUrls);
    return;
  }

  // Legacy shapes: whole objects, optionally TTL-wrapped as { data, cachedAt }.
  const paywallsTtlWrapped = !Array.isArray(rawPaywalls);
  const paywalls: NamiPaywall[] = paywallsTtlWrapped ? rawPaywalls.data : rawPaywalls;

  const paywallIds = new Set(paywalls.map((paywall: NamiPaywall) => paywall.id));
  for (const paywall of Object.values(testPaywallFiles)) {
    if (!paywallIds.has(paywall.id)) {
      paywalls.push(paywall);
    }
  }
  const paywallsToStore = paywallsTtlWrapped
    ? { data: paywalls, cachedAt: Date.now() }
    : paywalls;
  localStorage.setItem(API_PAYWALLS, JSON.stringify(paywallsToStore));
}

/** The NAM-4357 shape: each test paywall becomes a body-cache entry under a local URL. */
function augmentBodyCache(urls: string[]) {
  const cachedIds = new Set(urls.map((url) => /\/paywall\/([^/?#]+)/.exec(url)?.[1]));
  for (const paywall of Object.values(testPaywallFiles)) {
    const url = `${LOCAL_PAYWALL_URL_PREFIX}${paywall.id}/`;
    if (cachedIds.has(paywall.id) || urls.includes(url)) continue;
    localStorage.setItem(PAYWALL_BODY_KEY_PREFIX + url, JSON.stringify(paywall));
    localStorage.setItem(PAYWALL_BODY_META_KEY_PREFIX + url, JSON.stringify({ url, savedAt: Date.now() }));
    urls.push(url);
  }
  localStorage.setItem(API_PAYWALLS, JSON.stringify({ bodyCacheUrls: urls }));
}

function createCampaigns(paywallFiles: Record<string, NamiPaywall>): NamiCampaign[] {
  return Object.values(paywallFiles).map(file => ({
    rule: simpleUUID(),
    name: file.name,
    segment: file.id,
    paywall: file.id,
    type: 'label',
    value: file.name,
    external_segment: null,
    form_factors: [
        {
            "form_factor": "phone",
            "supports_portrait": true,
            "supports_landscape": false
        },
        {
            "form_factor": "tablet",
            "supports_portrait": true,
            "supports_landscape": true
        },
        {
            "form_factor": "desktop",
            "supports_portrait": true,
            "supports_landscape": true
        },
        {
            "form_factor": "television",
            "supports_portrait": false,
            "supports_landscape": true
        }
    ],
  }));
}
